import { Injectable } from '@angular/core';

const START_YEAR = 2012;

@Injectable({
  providedIn: 'root',
})
export class DateService {
  constructor() {}

  getYearsRange = (startYear: number, endYear: number): number[] => {
    const years: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  getYearsRangeUpToCurrentAsNumbers = () => this.getYearsRange(START_YEAR, this.getCurrentYear());
  getYearsRangeUpToCurrent = () => this.getYearsRangeUpToCurrentAsNumbers().map(year => year.toString());
  getMonthsRange = () => this.getMonthKeysAndValues().map(month => month.value);

  getCurrentYear = (): number => new Date().getFullYear();

  getRealLifeMonthNumber = (): string => (new Date().getMonth() + 1).toString().padStart(2, '0');

  getYearFromDate(date: string): string {
    return date.substring(0, 4);
  }

  getMonthFromDate(date: string): string {
    return date.substring(5);
  }

  getMonthKeysAndValues(): { name: string; value: string }[] {
    return [
      { name: 'January', value: '01' },
      { name: 'February', value: '02' },
      { name: 'March', value: '03' },
      { name: 'April', value: '04' },
      { name: 'May', value: '05' },
      { name: 'June', value: '06' },
      { name: 'July', value: '07' },
      { name: 'August', value: '08' },
      { name: 'September', value: '09' },
      { name: 'October', value: '10' },
      { name: 'November', value: '11' },
      { name: 'December', value: '12' },
    ];
  }

  getMonthName(month: string): string {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const index = parseInt(month, 10) - 1;
    return monthNames[index];
  }

  getDateFromMonthAndYear(monthDate: string, yearDate: string): Date {
    return new Date(Date.UTC(parseInt(yearDate, 10), parseInt(monthDate, 10) - 1, 1));
  }

  convertToTextMonths = (yearMonths: string[]): string[] => yearMonths.map(this.convertToTextMonth);

  convertToTextMonth = (yearMonth?: string): string => {
    if (!yearMonth) {
      return '';
    }
    const components = yearMonth.split('-');
    const monthNo = Number(components[1]);
    const date = new Date(Number(components[0]), monthNo - 1, 1);
    return `${date.toLocaleString('en-US', { month: 'short' })} ’${components[0].substring(2)}`;
  };

  yearMonthToIsoTimestampStart(yearMonth: string): string {
    if (!yearMonth) return '';
    return `${yearMonth}-01T00:00:00Z`;
  }

  yearMonthToIsoTimestampEnd(yearMonth: string): string {
    if (!yearMonth) return '';
    const [year, month] = yearMonth.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${yearMonth}-${lastDay.toString().padStart(2, '0')}T23:59:59Z`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatScoreDate(year?: number, month?: number): string {
    if (!year || !month) return 'N/A';
    const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    return `${monthNames[month - 1]} ${year}`;
  }

  formatDateTime(dateString?: string): string | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  formatUnixTimestamp(timestamp: string | null): string {
    if (!timestamp) return '—';
    try {
      const timestampNum = parseFloat(timestamp);
      if (isNaN(timestampNum)) return '—';
      const date = new Date(timestampNum * 1000);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
      return '—';
    }
  }
}
